import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  CircularProgress,
  IconButton,
  Pagination,
  Button,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { stripEnstruPrefix } from './utils';
import { useGetEnstruLookupQuery, useGetOktru2LookupQuery, useListPropertiesQuery } from '@/services/api';
import type { PropertyRegistryItem, AbcThresholds } from '@/types/api';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import PropertyViewer from './PropertyViewer';

const ITEMS_PER_PAGE = 50;
const DEBOUNCE_MS = 400;
const PREVIEW_CHIPS = 4;

type SortField = 'frequency';
type EnstruFilterOption = {
  code: string;
  frequency: number;
  name: string;
  characteristic: string;
};

type Oktru2FilterOption = {
  code: string;
  frequency: number;
  name: string;
};

const ENSTRU_CODE_PATTERN = /^\d{6}\.\d{3}\.\d{6}$/;
const FILTER_INPUT_HEIGHT = 56;

const normalizeEnstruCode = (code: string) => code.replace(/^ENSTRU:/, '');
const getClassifierCode = (code: string) => {
  const normalized = normalizeEnstruCode(code);
  const parts = normalized.split('.');
  return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : normalized;
};
const isDetailedEnstruCode = (code: string) => ENSTRU_CODE_PATTERN.test(normalizeEnstruCode(code));

// --- SABC analysis helpers ---
type AbcClass = 'S' | 'A' | 'B' | 'C';

/**
 * Classify a property by its global position in the frequency-sorted list.
 * SABC thresholds: S=0–40%, A=40–75%, B=75–95%, C=95–100%.
 * @param globalIndex 0-based position of the item (skip + row index on page)
 * @param abc SABC thresholds with count_s, count_a, and count_b
 */
const getAbcClassByPosition = (globalIndex: number, abc: AbcThresholds): AbcClass => {
  if (globalIndex < abc.count_s) return 'S';
  if (globalIndex < abc.count_s + abc.count_a) return 'A';
  if (globalIndex < abc.count_s + abc.count_a + abc.count_b) return 'B';
  return 'C';
};

const ABC_CONFIG: Record<AbcClass, { bg: string; text: string; nameEn: string; nameRu: string; labelEn: string; labelRu: string }> = {
  S: {
    bg: '#e3f2fd', text: '#1565c0',
    nameEn: 'Key', nameRu: 'Ключевые',
    labelEn: 'Key properties (0–40% of total frequency). Present in nearly every procurement of this product. Must be included in the specification.',
    labelRu: 'Ключевые свойства (0–40% суммарной частоты). Встречаются почти в каждой закупке этого товара. Обязательно включать в ТЗ.',
  },
  A: {
    bg: '#e8f5e9', text: '#2e7d32',
    nameEn: 'Important', nameRu: 'Важные',
    labelEn: 'Important properties (40–75% of total frequency). Frequently requested — recommended to include for a complete specification.',
    labelRu: 'Важные свойства (40–75% суммарной частоты). Часто запрашиваются — рекомендуется включить для полноты спецификации.',
  },
  B: {
    bg: '#fff3e0', text: '#e65100',
    nameEn: 'Supplementary', nameRu: 'Дополнительные',
    labelEn: 'Supplementary properties (75–95% of total frequency). Found in some procurements — useful for clarification.',
    labelRu: 'Дополнительные свойства (75–95% суммарной частоты). Встречаются в части закупок — полезны для уточнения.',
  },
  C: {
    bg: '#fce4ec', text: '#c62828',
    nameEn: 'Rare', nameRu: 'Редкие',
    labelEn: 'Rare properties (95–100% of total frequency). Used sporadically — only for specific requirements.',
    labelRu: 'Редкие свойства (95–100% суммарной частоты). Встречаются единично — только для специфических требований.',
  },
};

const PropertiesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useSelector((state: RootState) => state.ui);

  // Selected property is driven by URL — enables deep-linking & sharing
  const selectedPropertyId = searchParams.get('property') || null;

  // Read URL state
  const urlSearch = searchParams.get('search') || '';
  const urlSort = (searchParams.get('sort') || 'frequency') as SortField;
  const urlDir = searchParams.get('dir') || 'desc';
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const urlEnstruCode = searchParams.get('enstru_code') || '';
  const urlOktru2Code = searchParams.get('oktru2_code') || '';

  // Local input state (for debounce)
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);
  // Autocomplete key counters — increment to force-reset the input after chip delete
  const [enstruCodeResetKey, setEnstruCodeResetKey] = useState(0);
  const [oktru2CodeResetKey, setOktru2CodeResetKey] = useState(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Sync debounced search to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
    } else {
      params.delete('search');
    }
    // Reset to page 1 on search change
    params.set('page', '1');
    setSearchParams(params, { replace: true });
  }, [debouncedSearch]);

  // URL setters
  const updateParam = useCallback((key: string, value: string, resetPage = true) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (resetPage) {
      params.set('page', '1');
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSortChange = useCallback((field: SortField) => {
    const params = new URLSearchParams(searchParams);
    if (urlSort === field) {
      params.set('dir', urlDir === 'asc' ? 'desc' : 'asc');
    } else {
      params.set('sort', field);
      params.set('dir', 'desc');
    }
    params.set('page', '1');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams, urlSort, urlDir]);

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Determine if we're in "search mode" (relevance sorting, no custom sort)
  const isSearchMode = !!urlSearch;

  // Build API query params — server-side pagination
  const queryParams = useMemo(() => ({
    search: urlSearch || undefined,
    sort_by: isSearchMode ? undefined : urlSort,
    sort_dir: isSearchMode ? undefined : urlDir,
    top: ITEMS_PER_PAGE,
    skip: (urlPage - 1) * ITEMS_PER_PAGE,
    enstru_code: urlEnstruCode || undefined,
    oktru2_code: urlOktru2Code || undefined,
  }), [urlSearch, urlSort, urlDir, isSearchMode, urlPage, urlEnstruCode, urlOktru2Code]);

  // Separate stats query — loads ENSTRU and OKTRU2 code counts asynchronously (no blocking)
  const statsParams = useMemo(() => ({
    top: 1,
    skip: 0,
    include_enstru_stats: true,
    include_oktru2_stats: true,
  }), []);

  const {
    data,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useListPropertiesQuery(queryParams);
  const { data: statsData, refetch: refetchStats } = useListPropertiesQuery(statsParams);
  const { data: enstruLookup } = useGetEnstruLookupQuery();
  const { data: oktru2Lookup } = useGetOktru2LookupQuery();

  // Separate ABC query — depends only on ENSTRU/OKTRU2 filter, NOT on page/search.
  // RTK Query caches by key so pagination changes won't re-trigger the scan.
  const abcParams = useMemo(() => {
    if (!urlEnstruCode && !urlOktru2Code) return undefined;
    return {
      top: 1, skip: 0, include_abc: true,  // top:1 — only abc_thresholds matter, items[0] is ignored
      enstru_code: urlEnstruCode || undefined,
      oktru2_code: urlOktru2Code || undefined,
    };
  }, [urlEnstruCode, urlOktru2Code]);

  const { data: abcData } = useListPropertiesQuery(abcParams!, { skip: !abcParams });
  // Validate SABC data shape — guard against stale cache returning old format
  const rawAbc = abcData?.abc_thresholds;
  const abcThresholds = rawAbc
    && typeof rawAbc.count_s === 'number'
    && typeof rawAbc.count_a === 'number'
    && typeof rawAbc.count_b === 'number'
    ? rawAbc
    : undefined;

  const items = data?.items || [];

  // Max frequency: use global max from ABC when available, otherwise page-local.
  // This prevents visual contradiction where a C-class property fills 100% of the bar.
  const maxFrequency = useMemo(
    () => abcThresholds?.max_frequency ?? Math.max(1, ...items.map((p) => p.frequency ?? 0)),
    [items, abcThresholds]
  );

  // Compute range text
  const handlePropertyClick = useCallback((propertyId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('property', propertyId);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handlePropertyClose = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('property');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const formatDate = (value?: string) => (value ? value.split('T')[0] : '-');

  const renderOverflowChip = (label: string, items: string[]) => (
    <Tooltip
      title={
        <Box
          sx={{
            display: 'grid',
            gap: 0.5,
            py: 0.25,
            maxHeight: 'min(320px, calc(100vh - 96px))',
            overflowY: 'auto',
            pr: 0.5,
          }}
        >
          {items.map((item, index) => (
            <Typography key={`${item}-${index}`} variant="caption" sx={{ color: 'common.white' }}>
              {item}
            </Typography>
          ))}
        </Box>
      }
      enterDelay={0}
      enterNextDelay={0}
      leaveDelay={0}
      placement="top-start"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'flip',
              enabled: true,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                padding: 12,
                altAxis: true,
                tether: true,
              },
            },
          ],
        },
        tooltip: {
          sx: {
            maxWidth: 420,
            px: 1.25,
            py: 1,
            overflow: 'hidden',
          },
        },
      }}
    >
      <Chip label={label} size="small" sx={{ cursor: 'help' }} />
    </Tooltip>
  );

  const renderPreviewChips = (values: string[], emptyLabel: string) => {
    if (values.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      );
    }

    const visible = values.slice(0, PREVIEW_CHIPS);
    const remaining = values.length - visible.length;
    const hidden = values.slice(PREVIEW_CHIPS);

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {visible.map((value, index) => (
          <Chip key={`${value}-${index}`} label={value} size="small" variant="outlined" />
        ))}
        {remaining > 0 && (
          renderOverflowChip(`+${remaining}`, hidden)
        )}
      </Box>
    );
  };

  const renderEnstruCodes = (codes: string[]) => {
    const detailedCodes = Array.from(
      new Set(
        codes
          .map((code) => normalizeEnstruCode(code))
          .filter(isDetailedEnstruCode)
      )
    );

    if (detailedCodes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    const visible = detailedCodes.slice(0, 2);
    const remaining = detailedCodes.length - visible.length;
    const hidden = detailedCodes.slice(2).map((code) => {
      const details = enstruLookup?.[code];
      return [code, details?.name, details?.characteristic].filter(Boolean).join(' - ');
    });

    return (
      <Box sx={{ display: 'grid', gap: 1 }}>
        {visible.map((code) => {
          const details = enstruLookup?.[code];
          return (
            <Box key={code}>
              {details?.name && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {details.name}
                </Typography>
              )}
              {details?.characteristic && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {details.characteristic}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                ЕНСТРУ
              </Typography>
              <Tooltip title={(() => {
                const classifierCode = getClassifierCode(code);
                const classifierDetails = enstruLookup?.[classifierCode];
                return classifierDetails?.name || classifierCode;
              })()} arrow placement="top">
                <Chip
                  label={code}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 22, mt: 0.5 }}
                />
              </Tooltip>
            </Box>
          );
        })}
        {remaining > 0 && (
          renderOverflowChip(
            language === 'en' ? `+${remaining} more` : `+${remaining} еще`,
            hidden
          )
        )}
      </Box>
    );
  };


  const renderEnstruFilterOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: EnstruFilterOption,
  ) => {
    const { key, ...optionProps } = props;
    const title = option.name || (language === 'en' ? 'Unnamed ENSTRU code' : 'Код ЕНСТРУ без наименования');

    return (
      <Box
        component="li"
        {...optionProps}
        key={option.code}
        sx={{
          px: 0,
          py: 0,
          alignItems: 'stretch',
          '& + &': {
            mt: 0.5,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gridTemplateAreas: '"code freq" "name freq"',
            columnGap: 1.5,
            rowGap: 0.375,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              gridArea: 'code',
              fontFamily: 'monospace',
              letterSpacing: 0.2,
              lineHeight: 1.3,
              minWidth: 0,
            }}
          >
            {option.code}
          </Typography>

          <Chip
            size="small"
            label={
              language === 'en'
                ? `${option.frequency} properties`
                : `${option.frequency} свойств`
            }
            sx={{
              gridArea: 'freq',
              justifySelf: 'end',
              alignSelf: option.characteristic ? 'start' : 'center',
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              gridArea: 'name',
              lineHeight: 1.35,
              minWidth: 0,
              display: '-webkit-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            <Box component="span" sx={{ fontWeight: option.name ? 600 : 500, color: option.name ? 'text.primary' : 'text.secondary' }}>
              {title}
            </Box>
            {option.characteristic && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {' '}
                - {option.characteristic}
              </Box>
            )}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderOktru2FilterOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Oktru2FilterOption,
  ) => {
    const { key, ...optionProps } = props;
    const title = option.name || (language === 'en' ? 'Unnamed OKTRU2 code' : 'Код ОКТРУ2 без наименования');

    return (
      <Box
        component="li"
        {...optionProps}
        key={option.code}
        sx={{
          px: 0,
          py: 0,
          alignItems: 'stretch',
          '& + &': {
            mt: 0.5,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gridTemplateAreas: '"code freq" "name freq"',
            columnGap: 1.5,
            rowGap: 0.375,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              gridArea: 'code',
              fontFamily: 'monospace',
              letterSpacing: 0.2,
              lineHeight: 1.3,
              minWidth: 0,
            }}
          >
            {option.code}
          </Typography>

          <Chip
            size="small"
            label={
              language === 'en'
                ? `${option.frequency} properties`
                : `${option.frequency} свойств`
            }
            sx={{
              gridArea: 'freq',
              justifySelf: 'end',
              alignSelf: 'center',
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              gridArea: 'name',
              lineHeight: 1.35,
              minWidth: 0,
              display: '-webkit-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            <Box component="span" sx={{ fontWeight: option.name ? 600 : 500, color: option.name ? 'text.primary' : 'text.secondary' }}>
              {title}
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  };

  // Build filter options from backend ENSTRU stats (loaded asynchronously)
  const detailedEnstruStats = useMemo(() => {
    const stats = statsData?.enstru_stats;
    if (!stats) return [];

    return Object.entries(stats.codes)
      .map(([code, frequency]) => ({
        code,
        frequency,
        name: enstruLookup?.[code]?.name || '',
        characteristic: enstruLookup?.[code]?.characteristic || '',
      }))
      .sort((a, b) => b.frequency - a.frequency || a.code.localeCompare(b.code));
  }, [statsData, enstruLookup]);

  // Custom filter function for Autocomplete — searches across code, name, and characteristic
  const filterEnstruOptions = useCallback(
    (options: EnstruFilterOption[], { inputValue }: { inputValue: string }) => {
      const query = inputValue.trim().toLowerCase();
      if (!query) return options;
      return options.filter((entry) =>
        entry.code.toLowerCase().includes(query)
        || entry.name.toLowerCase().includes(query)
        || entry.characteristic.toLowerCase().includes(query)
      );
    },
    []
  );

  const selectedDetailedEnstruOption = useMemo(
    () => detailedEnstruStats.find((entry) => entry.code === urlEnstruCode) || null,
    [detailedEnstruStats, urlEnstruCode]
  );

  const selectedDetailedEnstruSummary = useMemo(() => {
    if (!selectedDetailedEnstruOption) {
      return '';
    }

    return [selectedDetailedEnstruOption.name, selectedDetailedEnstruOption.characteristic]
      .filter(Boolean)
      .join(' - ');
  }, [selectedDetailedEnstruOption]);

  // Build OKTRU2 filter options from backend OKTRU2 stats
  const oktru2FilterOptions = useMemo(() => {
    const stats = statsData?.oktru2_stats;
    if (!stats) return [];

    return Object.entries(stats.codes)
      .map(([code, frequency]) => ({
        code,
        frequency,
        name: oktru2Lookup?.[code]?.name || '',
      }))
      .sort((a, b) => b.frequency - a.frequency || a.code.localeCompare(b.code));
  }, [statsData, oktru2Lookup]);

  // Custom filter function for OKTRU2 Autocomplete — searches across code and name
  const filterOktru2Options = useCallback(
    (options: Oktru2FilterOption[], { inputValue }: { inputValue: string }) => {
      const query = inputValue.trim().toLowerCase();
      if (!query) return options;
      return options.filter((entry) =>
        entry.code.toLowerCase().includes(query)
        || entry.name.toLowerCase().includes(query)
      );
    },
    []
  );

  const selectedOktru2Option = useMemo(
    () => oktru2FilterOptions.find((entry) => entry.code === urlOktru2Code) || null,
    [oktru2FilterOptions, urlOktru2Code]
  );

  // Server-side pagination — use total from API response
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const currentPage = totalPages > 0 ? Math.min(urlPage, totalPages) : 1;
  const rangeStart = total > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const rangeEnd = Math.min(currentPage * ITEMS_PER_PAGE, total);

  if (error) {
    const errorMessage = 'data' in error && error.data
      ? (error.data as any).message || 'Failed to load properties'
      : 'Failed to load properties';

    return (
      <Box sx={{ p: 3, width: '100%' }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {language === 'en' ? 'Error Loading Properties' : 'Ошибка загрузки свойств'}
          </Typography>
          <Typography variant="body2">{errorMessage}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%', minWidth: 0, overflowX: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 1,
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0 }}>
            {language === 'en'
              ? 'Browse canonical property definitions used for normalizing procurement specifications.'
              : 'Просмотр канонических определений свойств, используемых для нормализации закупочных спецификаций.'}
          </Typography>
          <Button
            variant="outlined"
            size="medium"
            startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => { refetch(); refetchStats(); }}
            disabled={isFetching}
            sx={{ flexShrink: 0 }}
          >
            {language === 'en' ? 'Refresh' : 'Обновить'}
          </Button>
        </Box>

        {/* Search and Filters */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            variant="outlined"
            placeholder={language === 'en' ? 'Search properties...' : 'Поиск свойств...'}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {isFetching ? (
                    <CircularProgress size={20} />
                  ) : searchInput ? (
                    <IconButton
                      size="small"
                      onClick={() => setSearchInput('')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 384 },
              flex: '0 1 384px',
              '& .MuiOutlinedInput-root': {
                height: FILTER_INPUT_HEIGHT,
              },
            }}
          />

          <Autocomplete
            key={`enstru-${enstruCodeResetKey}`}
            options={detailedEnstruStats}
            value={selectedDetailedEnstruOption}
            onChange={(_, value) => updateParam('enstru_code', value?.code || '')}
            getOptionLabel={(option) => `${option.code} ${option.name}`.trim()}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            filterOptions={filterEnstruOptions}
            noOptionsText={
              language === 'en' ? 'No ENSTRU codes found' : 'Коды ЕНСТРУ не найдены'
            }
            sx={{
              width: { xs: '100%', sm: 384, md: 408 },
              flex: '0 1 408px',
              '& .MuiOutlinedInput-root': {
                height: FILTER_INPUT_HEIGHT,
              },
              '& .MuiInputBase-input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
            slotProps={{
              popper: {
                sx: {
                  '&[data-popper-placement]': {
                    minWidth: { sm: '620px', md: '720px' },
                    maxWidth: 'min(920px, calc(100vw - 48px))',
                  },
                },
              },
              paper: {
                elevation: 8,
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  overflow: 'hidden',
                },
              },
              listbox: {
                sx: {
                  p: 0.5,
                  maxHeight: 420,
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={
                  language === 'en'
                    ? `ENSTRU code (${detailedEnstruStats.length})`
                    : `Код ЕНСТРУ (${detailedEnstruStats.length})`
                }
                placeholder={
                  language === 'en'
                    ? 'Search detailed ENSTRU code, name, or description...'
                    : 'Поиск детального кода ЕНСТРУ по коду, наименованию или описанию...'
                }
              />
            )}
            renderOption={renderEnstruFilterOption}
          />

          {urlEnstruCode && (
            <Chip
              size="small"
              color="primary"
              label={urlEnstruCode}
              onDelete={() => {
                updateParam('enstru_code', '');
                setEnstruCodeResetKey((k) => k + 1);
              }}
            />
          )}

          <Autocomplete
            key={`oktru2-${oktru2CodeResetKey}`}
            options={oktru2FilterOptions}
            value={selectedOktru2Option}
            onChange={(_, value) => updateParam('oktru2_code', value?.code || '')}
            getOptionLabel={(option) => `${option.code} ${option.name}`.trim()}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            filterOptions={filterOktru2Options}
            noOptionsText={
              language === 'en' ? 'No OKTRU2 codes found' : 'Коды ОКТРУ2 не найдены'
            }
            sx={{
              width: { xs: '100%', sm: 384, md: 408 },
              flex: '0 1 408px',
              '& .MuiOutlinedInput-root': {
                height: FILTER_INPUT_HEIGHT,
              },
              '& .MuiInputBase-input': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
            slotProps={{
              popper: {
                sx: {
                  '&[data-popper-placement]': {
                    minWidth: { sm: '620px', md: '720px' },
                    maxWidth: 'min(920px, calc(100vw - 48px))',
                  },
                },
              },
              paper: {
                elevation: 8,
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  overflow: 'hidden',
                },
              },
              listbox: {
                sx: {
                  p: 0.5,
                  maxHeight: 420,
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={
                  language === 'en'
                    ? `OKTRU2 code (${oktru2FilterOptions.length})`
                    : `Код ОКТРУ2 (${oktru2FilterOptions.length})`
                }
                placeholder={
                  language === 'en'
                    ? 'Search OKTRU2 code or name...'
                    : 'Поиск кода ОКТРУ2 по коду или наименованию...'
                }
              />
            )}
            renderOption={renderOktru2FilterOption}
          />

          {urlOktru2Code && (
            <Chip
              size="small"
              color="secondary"
              label={urlOktru2Code}
              onDelete={() => {
                updateParam('oktru2_code', '');
                setOktru2CodeResetKey((k) => k + 1);
              }}
            />
          )}

        </Box>
      </Box>

      {!isLoading && (
        <Box
          sx={{
            mt: 1,
            mb: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
          >
            {language === 'en'
              ? `Showing ${rangeStart}–${rangeEnd} of ${total} properties`
              : `Показано ${rangeStart}–${rangeEnd} из ${total} свойств`}
            {urlSearch && (language === 'en' ? ` matching "${urlSearch}"` : ` по запросу "${urlSearch}"`)}
            {urlEnstruCode && (
              language === 'en'
                ? ` in ENSTRU code ${urlEnstruCode}${selectedDetailedEnstruSummary ? ` (${selectedDetailedEnstruSummary})` : ''}`
                : ` по коду ЕНСТРУ ${urlEnstruCode}${selectedDetailedEnstruSummary ? ` (${selectedDetailedEnstruSummary})` : ''}`
            )}
            {urlOktru2Code && (
              language === 'en'
                ? ` in OKTRU2 code ${urlOktru2Code}${selectedOktru2Option?.name ? ` (${selectedOktru2Option.name})` : ''}`
                : ` по коду ОКТРУ2 ${urlOktru2Code}${selectedOktru2Option?.name ? ` (${selectedOktru2Option.name})` : ''}`
            )}
          </Typography>
          {abcThresholds && (() => {
            const countC = abcThresholds.property_count - abcThresholds.count_s - abcThresholds.count_a - abcThresholds.count_b;
            const isEn = language === 'en';
            return (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Tooltip title={isEn ? ABC_CONFIG.S.labelEn : ABC_CONFIG.S.labelRu} arrow placement="top">
                  <Chip label={`${isEn ? ABC_CONFIG.S.nameEn : ABC_CONFIG.S.nameRu} ${abcThresholds.count_s}`} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, bgcolor: ABC_CONFIG.S.bg, color: ABC_CONFIG.S.text, cursor: 'help', '& .MuiChip-label': { px: 0.75 } }} />
                </Tooltip>
                <Tooltip title={isEn ? ABC_CONFIG.A.labelEn : ABC_CONFIG.A.labelRu} arrow placement="top">
                  <Chip label={`${isEn ? ABC_CONFIG.A.nameEn : ABC_CONFIG.A.nameRu} ${abcThresholds.count_a}`} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, bgcolor: ABC_CONFIG.A.bg, color: ABC_CONFIG.A.text, cursor: 'help', '& .MuiChip-label': { px: 0.75 } }} />
                </Tooltip>
                <Tooltip title={isEn ? ABC_CONFIG.B.labelEn : ABC_CONFIG.B.labelRu} arrow placement="top">
                  <Chip label={`${isEn ? ABC_CONFIG.B.nameEn : ABC_CONFIG.B.nameRu} ${abcThresholds.count_b}`} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, bgcolor: ABC_CONFIG.B.bg, color: ABC_CONFIG.B.text, cursor: 'help', '& .MuiChip-label': { px: 0.75 } }} />
                </Tooltip>
                <Tooltip title={isEn ? ABC_CONFIG.C.labelEn : ABC_CONFIG.C.labelRu} arrow placement="top">
                  <Chip label={`${isEn ? ABC_CONFIG.C.nameEn : ABC_CONFIG.C.nameRu} ${countC}`} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, bgcolor: ABC_CONFIG.C.bg, color: ABC_CONFIG.C.text, cursor: 'help', '& .MuiChip-label': { px: 0.75 } }} />
                </Tooltip>
              </Typography>
            );
          })()}
        </Box>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 1,
          flex: 1,
          minHeight: 0,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Table
          sx={{
            minWidth: 1200,
            width: '100%',
            tableLayout: 'fixed',
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 280, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <strong>{language === 'en' ? 'Name' : 'Название'}</strong>
              </TableCell>
              <TableCell sx={{ width: abcThresholds ? 160 : 100, maxWidth: abcThresholds ? 160 : 100, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <Tooltip
                  title={abcThresholds
                    ? (language === 'en'
                      ? 'Importance analysis by usage frequency: Key (0–40%) — must include, Important (40–75%) — recommended, Supplementary (75–95%) — as needed, Rare (95–100%) — specific cases only'
                      : 'Анализ важности по частоте использования: Ключевые (0–40%) — обязательно, Важные (40–75%) — рекомендуется, Дополнительные (75–95%) — по необходимости, Редкие (95–100%) — специфические случаи')
                    : ''
                  }
                  arrow
                  placement="top"
                >
                  <span>
                    {isSearchMode ? (
                      <strong>{language === 'en' ? 'Frequency' : 'Частота'}</strong>
                    ) : (
                      <TableSortLabel
                        active={urlSort === 'frequency'}
                        direction={urlSort === 'frequency' ? urlDir as 'asc' | 'desc' : 'desc'}
                        onClick={() => handleSortChange('frequency')}
                      >
                        <strong>{language === 'en' ? 'Frequency' : 'Частота'}</strong>
                      </TableSortLabel>
                    )}
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ minWidth: 220, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <strong>{language === 'en' ? 'Structure' : 'Структура'}</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 260, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <strong>{language === 'en' ? 'Synonyms' : 'Синонимы'}</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 260, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <strong>{language === 'en' ? 'Examples' : 'Примеры'}</strong>
              </TableCell>
              <TableCell sx={{ minWidth: 220, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                <strong>{language === 'en' ? 'Product' : 'Товар'}</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="45%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Alert severity="info">
                    {urlSearch
                      ? (language === 'en'
                        ? `No properties match "${urlSearch}".`
                        : `Свойства не найдены по запросу "${urlSearch}".`)
                      : (language === 'en'
                        ? 'No properties found.'
                        : 'Свойства не найдены.')}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              items.map((prop: PropertyRegistryItem, rowIndex: number) => {
                // SABC position: map row to frequency-descending index.
                // When sorting ascending, reverse so position 0 (lowest freq) maps to the tail.
                const pageIdx = (urlPage - 1) * ITEMS_PER_PAGE + rowIndex;
                const freqDescIdx = (!isSearchMode && urlSort === 'frequency' && urlDir === 'asc')
                  ? Math.max(0, total - 1 - pageIdx)
                  : pageIdx;

                return (
                <TableRow
                  key={prop.property_id}
                  hover
                  sx={{
                    verticalAlign: 'top',
                    opacity: prop.deprecated ? 0.6 : 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => handlePropertyClick(prop.property_id)}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {language === 'en'
                        ? (prop.canonical_name_en || prop.canonical_name_ru || stripEnstruPrefix(prop.property_id))
                        : (prop.canonical_name_ru || prop.canonical_name_en || stripEnstruPrefix(prop.property_id))}
                    </Typography>
                    {prop.canonical_name_en && prop.canonical_name_ru && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {language === 'en' ? prop.canonical_name_ru : prop.canonical_name_en}
                      </Typography>
                    )}
                    {prop.deprecated && (
                      <Chip label="deprecated" size="small" color="error" variant="outlined" sx={{ mt: 0.5 }} />
                    )}
                    {prop.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {prop.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ pr: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                        {abcThresholds ? (() => {
                          const abc = getAbcClassByPosition(freqDescIdx, abcThresholds);
                          const cfg = ABC_CONFIG[abc];
                          const chipLabel = language === 'en' ? cfg.nameEn : cfg.nameRu;
                          return (
                            <Tooltip
                              title={language === 'en' ? cfg.labelEn : cfg.labelRu}
                              arrow
                              placement="left"
                            >
                              <Chip
                                label={chipLabel}
                                size="small"
                                aria-label={`${language === 'en' ? 'Importance class' : 'Класс важности'}: ${chipLabel}`}
                                sx={{
                                  height: 22,
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  bgcolor: cfg.bg,
                                  color: cfg.text,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </Tooltip>
                          );
                        })() : <span />}
                        <Typography
                          variant="body2"
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {(prop.frequency ?? 0).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'action.hover',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.round(((prop.frequency ?? 0) / maxFrequency) * 100)}%`,
                            borderRadius: 3,
                            bgcolor: abcThresholds
                              ? ABC_CONFIG[getAbcClassByPosition(freqDescIdx, abcThresholds)].text
                              : 'primary.main',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {language === 'en' ? 'Key' : 'Ключ'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, mb: 1, wordBreak: 'break-all' }}>
                      {stripEnstruPrefix(prop.property_id)}
                    </Typography>
                    <Chip
                      label={prop.data_type}
                      size="small"
                      color={
                        prop.data_type === 'measure' ? 'primary' :
                        prop.data_type === 'enum' ? 'secondary' :
                        prop.data_type === 'boolean' ? 'success' :
                        'default'
                      }
                      variant="outlined"
                    />
                    {prop.data_type !== 'enum' && (
                      <>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {language === 'en' ? 'Unit' : 'Единица'}
                          </Typography>
                          <Typography variant="body2">
                            {prop.unit_ucum || '-'}
                          </Typography>
                        </Box>
                        {prop.unit_allowed.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {language === 'en' ? 'Allowed' : 'Допустимые'}
                            </Typography>
                            {renderPreviewChips(prop.unit_allowed, '-')}
                          </Box>
                        )}
                      </>
                    )}
                    {prop.data_type === 'enum' && prop.enum_values.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {language === 'en' ? 'Enum values' : 'Значения enum'}
                        </Typography>
                        {renderPreviewChips(
                          prop.enum_values.map((v) => v.label_ru || v.label_en || v.id),
                          '-'
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {renderPreviewChips(
                      [...prop.synonyms_ru, ...prop.synonyms_kz],
                      language === 'en' ? 'No synonyms' : 'Нет синонимов'
                    )}
                  </TableCell>
                  <TableCell>
                    {renderPreviewChips(
                      prop.examples,
                      language === 'en' ? 'No examples' : 'Нет примеров'
                    )}
                  </TableCell>
                  <TableCell>
                    {renderEnstruCodes(prop.enstru_codes)}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Box sx={{ mt: 1.5, py: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 2, flexShrink: 0 }}>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      )}

      {/* Property detail drawer */}
      <PropertyViewer
        propertyId={selectedPropertyId}
        onClose={handlePropertyClose}
      />
    </Box>
  );
};

export default PropertiesPage;
